/**
 * The bridge to the native app that hosts this build.
 *
 * ❓ PLACEHOLDER — none of this is in the decomp. The shipped game talks to
 * `RollicGames.Advertisements.RLAdvertisementManager` in-process; a web port has
 * no such SDK, so the wrapper app owns the ad and billing SDKs and this file is
 * the only way to reach it. Shape copied from `reference/DiceFlow/index.html`
 * (see `web/docs/ADS-MONETIZATION.md` §A.2.1), which is a shipped port of the
 * same house, so the protocol is at least one the wrapper already speaks.
 *
 * Two directions:
 *   out  `window.AndroidBridge.postMessage(JSON.stringify({ action, data }))`
 *   in   `window.onNativeMessage(event, data)` — one entry point for everything
 *
 * Requests carry a `callbackId` so a reply can be matched to its caller. A few
 * events arrive *without* one (`remove_ads_changed`, `network_changed`,
 * `app_state_changed`): the app pushing state at us, not answering a question.
 * The two kinds are handled apart on purpose — a push has no promise to
 * resolve, and a reply to a question nobody asked must not resolve one.
 */

/** Anything the app sends back. Fields depend on the event. */
export interface NativeMessage {
  callbackId?: string
  [key: string]: unknown
}

/** The object the wrapper injects. Absent in a plain browser. */
export interface NativeHost {
  postMessage(payload: string): void
}

interface BridgeWindow {
  AndroidBridge?: NativeHost
  onNativeMessage?: (event: string, data?: NativeMessage) => void
}

/**
 * Replies that resolve a pending request. Anything not listed is ignored, so a
 * future app version cannot resolve a promise we did not make.
 */
const RESPONSE_EVENTS = new Set([
  'interstitial_closed',
  'products',
  'purchase_result',
  'remove_ads_status',
  'restore_result',
  // WebBridge.md §2 — the cloud save's two replies. `user_data` answers
  // `request_user_data`, `user_data_saved` answers `save_user_data`.
  'user_data',
  'user_data_saved',
])

/** Pushed by the app on its own initiative — no `callbackId` to match. */
const REMOVE_ADS_CHANGED = 'remove_ads_changed'
/** WebBridge.md §2 — connectivity flipped. `{ online: boolean }`. */
const NETWORK_CHANGED = 'network_changed'
/** WebBridge.md §2 — foreground/background flipped. `{ active: boolean }`. */
const APP_STATE_CHANGED = 'app_state_changed'

export class NativeBridge {
  private readonly pending = new Map<string, (data: NativeMessage) => void>()
  private readonly removeAdsListeners: ((purchased: boolean) => void)[] = []
  private readonly networkListeners: ((online: boolean) => void)[] = []
  private readonly appStateListeners: ((active: boolean) => void)[] = []
  private seq = 0

  constructor(
    private readonly host: BridgeWindow,
    private readonly now: () => number = Date.now,
  ) {
    host.onNativeMessage = (event, data) => this.receive(event, data)
  }

  /** False in a plain browser: every request then takes its offline fallback. */
  get isNative(): boolean {
    return typeof this.host.AndroidBridge?.postMessage === 'function'
  }

  /** Fire and forget — analytics, and the half of a request that goes out. */
  send(action: string, data: Record<string, unknown> = {}): void {
    try {
      if (this.isNative) this.host.AndroidBridge!.postMessage(JSON.stringify({ action, data }))
      else console.log('[→ native]', action, data)
    } catch {
      // A bridge that throws must not take a button handler down with it.
    }
  }

  /**
   * Send and wait for the app's reply.
   *
   * `offline` is what a plain browser answers with. It is not a stub for
   * convenience: it decides what `npm run dev` does, and the two ad paths want
   * opposite answers (an interstitial cannot be shown, so `shown: false`).
   */
  request(
    action: string,
    tag: string,
    data: Record<string, unknown>,
    offline: NativeMessage,
  ): Promise<NativeMessage> {
    return new Promise((resolve) => {
      const callbackId = `${tag}_${++this.seq}_${this.now()}`
      this.pending.set(callbackId, (reply) => {
        this.pending.delete(callbackId)
        resolve(reply)
      })
      this.send(action, { ...data, callbackId })
      if (!this.isNative) {
        // Next tick, so a caller always sees an async reply either way.
        setTimeout(() => this.receive('offline', { ...offline, callbackId }), 0)
      }
    })
  }

  /** The app telling us the entitlement changed — a purchase on another device. */
  onRemoveAdsChanged(listener: (purchased: boolean) => void): void {
    this.removeAdsListeners.push(listener)
  }

  /** WebBridge.md §2 `network_changed` — the app reporting connectivity. */
  onNetworkChanged(listener: (online: boolean) => void): void {
    this.networkListeners.push(listener)
  }

  /**
   * WebBridge.md §2 `app_state_changed` — foreground on, background off.
   *
   * Load-bearing rather than decorative: `log_level.duration` has to be the
   * time the player was *actually playing*, and on a device this is the only
   * signal that says the app went away. See `levelLog.ts` and rule_LOGLEVEL §4d.
   */
  onAppStateChanged(listener: (active: boolean) => void): void {
    this.appStateListeners.push(listener)
  }

  private receive(event: string, data?: NativeMessage): void {
    if (event === REMOVE_ADS_CHANGED) {
      const purchased = data?.purchased === true
      for (const listener of this.removeAdsListeners) listener(purchased)
      return
    }
    if (event === NETWORK_CHANGED) {
      const online = data?.online === true
      for (const listener of this.networkListeners) listener(online)
      return
    }
    if (event === APP_STATE_CHANGED) {
      const active = data?.active === true
      for (const listener of this.appStateListeners) listener(active)
      return
    }
    // `offline` is our own synthetic reply and is always allowed through; every
    // real event has to be one we asked for.
    if (event !== 'offline' && !RESPONSE_EVENTS.has(event)) return
    const callbackId = data?.callbackId
    if (typeof callbackId !== 'string') return
    this.pending.get(callbackId)?.(data ?? {})
  }
}

/** The bridge this build actually uses. */
export const nativeBridge = new NativeBridge(window as unknown as BridgeWindow)
